import {
    HelixSearchFilterExperienceTimeframe,
    HelixSearchFilterKeywordLogic,
    HelixSearchFilters,
} from '../../../api/helixSearchV2/models/HelixSearchFilters'

export type KeywordLogic = HelixSearchFilterKeywordLogic

export interface KeywordGroup {
    keywords: string[]
}

export type SearchInvocationOptions = {
    source?: 'filters_modal'
}

export type Seniority =
    | 'C-Level'
    | 'Director'
    | 'Founder'
    | 'Head'
    | 'Intern'
    | 'Manager'
    | 'Owner'
    | 'Partner'
    | 'President/Vice President'
    | 'Senior'
    | 'Specialist'
    | 'Vice President'

export const SENIORITY_VALUES: readonly Seniority[] = [
    'C-Level',
    'Director',
    'Founder',
    'Head',
    'Intern',
    'Manager',
    'Owner',
    'Partner',
    'President/Vice President',
    'Senior',
    'Specialist',
    'Vice President',
] as const

export type JobFunction =
    | 'Administrative'
    | 'Consulting'
    | 'C-Suite'
    | 'Customer Service'
    | 'Design'
    | 'Education'
    | 'Engineering and Technical'
    | 'Finance & Accounting'
    | 'Human Resources'
    | 'Legal'
    | 'Marketing'
    | 'Medical'
    | 'Operations'
    | 'Other'
    | 'Product'
    | 'Project Management'
    | 'Real Estate'
    | 'Research'
    | 'Sales'
    | 'Trades'

export const JOB_FUNCTION_VALUES: readonly JobFunction[] = [
    'Administrative',
    'Consulting',
    'C-Suite',
    'Customer Service',
    'Design',
    'Education',
    'Engineering and Technical',
    'Finance & Accounting',
    'Human Resources',
    'Legal',
    'Marketing',
    'Medical',
    'Operations',
    'Other',
    'Product',
    'Project Management',
    'Real Estate',
    'Research',
    'Sales',
    'Trades',
] as const

export type ExperienceTimeframe = HelixSearchFilterExperienceTimeframe

export interface FilterJobTitles {
    include: string[]
    includeTimeframe: ExperienceTimeframe
    exclude: string[]
    excludeTimeframe: ExperienceTimeframe
}

export interface FilterCompanies {
    include: string[]
    includeTimeframe: ExperienceTimeframe
    exclude: string[]
    excludeTimeframe: ExperienceTimeframe
}

export interface ExperienceBounds {
    minYears: number
    maxYears: number
}

export interface FilterInputs {
    trustedCirclePersonIds: string[]
    networkSeedPersonIds: string[]
    jobTitles: FilterJobTitles
    keywordGroups: KeywordGroup[]
    keywordLogic: KeywordLogic
    companies: FilterCompanies
    seniorities: Seniority[]
    jobFunctions: JobFunction[]
    locations: string[]
}

export const DEFAULT_COMPANY_INCLUDE_VALUES: readonly string[] = [
    'Google',
    'Microsoft',
    'Amazon',
    'Facebook',
    'Apple',
] as const

export const DEFAULT_COMPANY_EXCLUDE_VALUES: readonly string[] = [
    'Brevan Howard',
] as const

export const COMPANY_FILTER_VALUES: readonly string[] = [
    ...DEFAULT_COMPANY_INCLUDE_VALUES,
    ...DEFAULT_COMPANY_EXCLUDE_VALUES,
    'Adobe',
    'Airbnb',
    'Coinbase',
    'Datadog',
    'DoorDash',
    'Dropbox',
    'GitHub',
    'IBM',
    'Intel',
    'LinkedIn',
    'Lyft',
    'Meta',
    'Netflix',
    'Nvidia',
    'OpenAI',
    'Oracle',
    'Palantir',
    'Salesforce',
    'Shopify',
    'Snowflake',
    'SpaceX',
    'Stripe',
    'Tesla',
    'Uber',
] as const

export const createDefaultFilterInputs = (
    trustedCirclePersonIds: string[] = [],
): FilterInputs => {
    const trustedCircleIds = [...trustedCirclePersonIds]

    return {
        trustedCirclePersonIds: trustedCircleIds,
        networkSeedPersonIds: trustedCircleIds,
        jobTitles: {
            include: [],
            includeTimeframe: 'past_only',
            exclude: [],
            excludeTimeframe: 'current_or_past',
        },
        keywordGroups: [],
        keywordLogic: 'AND',
        companies: {
            include: [],
            includeTimeframe: 'current_or_past',
            exclude: [],
            excludeTimeframe: 'current_only',
        },
        seniorities: [],
        jobFunctions: [],
        locations: [],
    }
}

const hasArrayValues = (values?: string[]): boolean =>
    Array.isArray(values) && values.length > 0

const sanitizeKeywordGroups = (groups: KeywordGroup[]): KeywordGroup[] =>
    groups
        .map((group) => ({
            keywords: group.keywords
                .map((keyword) => keyword.trim())
                .filter((keyword) => keyword.length > 0),
        }))
        .filter((group) => group.keywords.length > 0)

const filterValidSeniorities = (values?: string[]): Seniority[] => {
    if (!values || values.length === 0) {
        return []
    }

    const allowed = new Set<string>(SENIORITY_VALUES as readonly string[])
    return values
        .map((value) => value.trim())
        .filter((value): value is Seniority => allowed.has(value))
}

export const filterValidJobFunctions = (
    values?: (string | null | undefined)[],
): JobFunction[] => {
    if (!values || values.length === 0) {
        return []
    }

    const allowed = new Set<string>(JOB_FUNCTION_VALUES as readonly string[])
    return values
        .map((value) => (value ?? '').trim())
        .filter((value): value is JobFunction => allowed.has(value))
}

export const buildHelixSearchFiltersPayload = (
    filters: FilterInputs,
): HelixSearchFilters => {
    const payload: HelixSearchFilters = {}

    if (hasArrayValues(filters.jobTitles.include)) {
        payload.jobTitlesInclude = [...filters.jobTitles.include]
    }
    if (filters.jobTitles.includeTimeframe) {
        payload.jobTitlesIncludeTimeframe = filters.jobTitles.includeTimeframe
    }
    if (hasArrayValues(filters.jobTitles.exclude)) {
        payload.jobTitlesExclude = [...filters.jobTitles.exclude]
    }
    if (filters.jobTitles.excludeTimeframe) {
        payload.jobTitlesExcludeTimeframe = filters.jobTitles.excludeTimeframe
    }

    const cleanedKeywordGroups = sanitizeKeywordGroups(filters.keywordGroups)
    if (cleanedKeywordGroups.length > 0) {
        payload.keywordGroups = cleanedKeywordGroups
    }

    if (filters.keywordLogic) {
        payload.keywordLogic = filters.keywordLogic
    }

    if (hasArrayValues(filters.locations)) {
        payload.locations = [...filters.locations]
    }

    if (hasArrayValues(filters.companies.include)) {
        payload.companiesInclude = [...filters.companies.include]
    }
    if (filters.companies.includeTimeframe) {
        payload.companiesIncludeTimeframe = filters.companies.includeTimeframe
    }
    if (hasArrayValues(filters.companies.exclude)) {
        payload.companiesExclude = [...filters.companies.exclude]
    }
    if (filters.companies.excludeTimeframe) {
        payload.companiesExcludeTimeframe = filters.companies.excludeTimeframe
    }

    if (hasArrayValues(filters.seniorities)) {
        payload.seniorities = [...filters.seniorities]
    }

    if (hasArrayValues(filters.jobFunctions)) {
        payload.departments = [...filters.jobFunctions]
    }

    return payload
}

export const applyHelixSearchFilters = (
    baseFilters: FilterInputs,
    apiFilters?: HelixSearchFilters | null,
): FilterInputs => {
    if (!apiFilters) {
        return {
            ...baseFilters,
            jobTitles: { ...baseFilters.jobTitles },
            keywordGroups: baseFilters.keywordGroups.map((group) => ({
                keywords: [...group.keywords],
            })),
            companies: { ...baseFilters.companies },
            seniorities: [...baseFilters.seniorities],
            jobFunctions: [...baseFilters.jobFunctions],
            locations: [...baseFilters.locations],
        }
    }

    const next: FilterInputs = {
        ...baseFilters,
        jobTitles: {
            include: [...baseFilters.jobTitles.include],
            includeTimeframe: baseFilters.jobTitles.includeTimeframe,
            exclude: [...baseFilters.jobTitles.exclude],
            excludeTimeframe: baseFilters.jobTitles.excludeTimeframe,
        },
        keywordGroups: baseFilters.keywordGroups.map((group) => ({
            keywords: [...group.keywords],
        })),
        companies: {
            include: [...baseFilters.companies.include],
            includeTimeframe: baseFilters.companies.includeTimeframe,
            exclude: [...baseFilters.companies.exclude],
            excludeTimeframe: baseFilters.companies.excludeTimeframe,
        },
        seniorities: [...baseFilters.seniorities],
        jobFunctions: [...baseFilters.jobFunctions],
        locations: [...baseFilters.locations],
    }

    if (apiFilters.jobTitlesInclude) {
        next.jobTitles.include = [...apiFilters.jobTitlesInclude]
    }
    if (apiFilters.jobTitlesIncludeTimeframe) {
        next.jobTitles.includeTimeframe = apiFilters.jobTitlesIncludeTimeframe
    }
    if (apiFilters.jobTitlesExclude) {
        next.jobTitles.exclude = [...apiFilters.jobTitlesExclude]
    }
    if (apiFilters.jobTitlesExcludeTimeframe) {
        next.jobTitles.excludeTimeframe = apiFilters.jobTitlesExcludeTimeframe
    }

    if (apiFilters.keywordGroups) {
        next.keywordGroups = sanitizeKeywordGroups(
            apiFilters.keywordGroups.map((group) => ({
                keywords: [...(group.keywords ?? [])],
            })),
        )
    }

    if (apiFilters.keywordLogic) {
        next.keywordLogic = apiFilters.keywordLogic
    }

    if (apiFilters.locations) {
        next.locations = [...apiFilters.locations]
    }

    if (apiFilters.companiesInclude) {
        next.companies.include = [...apiFilters.companiesInclude]
    }
    if (apiFilters.companiesIncludeTimeframe) {
        next.companies.includeTimeframe = apiFilters.companiesIncludeTimeframe
    }
    if (apiFilters.companiesExclude) {
        next.companies.exclude = [...apiFilters.companiesExclude]
    }
    if (apiFilters.companiesExcludeTimeframe) {
        next.companies.excludeTimeframe = apiFilters.companiesExcludeTimeframe
    }

    if (apiFilters.seniorities) {
        next.seniorities = filterValidSeniorities(apiFilters.seniorities)
    }

    if (apiFilters.departments) {
        next.jobFunctions = filterValidJobFunctions(apiFilters.departments)
    }

    return next
}

const COMPLEX_KEYWORD_PATTERN = new RegExp(
    '[:(){}\\[\\]"\\\\*?]|(?:\\bAND\\b|\\bOR\\b|\\bNOT\\b)|&&|\\|\\|',
    'i',
)

const escapeLuceneValue = (value: string): string =>
    value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')

const buildFieldClause = (values: string[], field: string): string | null => {
    const clauses = values
        .map((value) => value.trim())
        .filter((value) => value.length > 0)
        .map((value) => `${field}:"${escapeLuceneValue(value)}"`)

    if (clauses.length === 0) {
        return null
    }

    if (clauses.length === 1) {
        return clauses[0]
    }

    return `(${clauses.join(' OR ')})`
}

const shouldTreatKeywordAsRaw = (value: string): boolean =>
    COMPLEX_KEYWORD_PATTERN.test(value)

const formatKeywordValue = (value: string): string | null => {
    const trimmed = value.trim()

    if (trimmed.length === 0) {
        return null
    }

    if (shouldTreatKeywordAsRaw(trimmed)) {
        if (
            trimmed.startsWith('(') &&
            trimmed.endsWith(')') &&
            trimmed.length > 2
        ) {
            return trimmed
        }
        return `(${trimmed})`
    }

    return `"${escapeLuceneValue(trimmed)}"`
}

const buildKeywordGroupClause = (group: KeywordGroup): string | null => {
    const keywordClauses = group.keywords
        .map(formatKeywordValue)
        .filter((clause): clause is string => clause !== null)

    if (keywordClauses.length === 0) {
        return null
    }

    if (keywordClauses.length === 1) {
        return keywordClauses[0]
    }

    return `(${keywordClauses.join(' OR ')})`
}

const buildKeywordClause = (
    groups: KeywordGroup[],
    logic: KeywordLogic,
): string | null => {
    const sanitizedGroups = sanitizeKeywordGroups(groups)
    const clauses = sanitizedGroups
        .map(buildKeywordGroupClause)
        .filter((clause): clause is string => clause !== null)

    if (clauses.length === 0) {
        return null
    }

    const operator = logic === 'OR' ? 'OR' : 'AND'

    if (clauses.length === 1) {
        return clauses[0]
    }

    return `(${clauses.join(` ${operator} `)})`
}

export const buildBooleanQueryFromFilters = (filters: FilterInputs): string => {
    const mustClauses: string[] = []
    const mustNotClauses: string[] = []

    const keywordClause = buildKeywordClause(
        filters.keywordGroups,
        filters.keywordLogic ?? 'AND',
    )
    if (keywordClause) {
        mustClauses.push(keywordClause)
    }

    const seniorityClause = buildFieldClause(
        filters.seniorities,
        'managementLevel',
    )
    if (seniorityClause) {
        mustClauses.push(seniorityClause)
    }

    const departmentClause = buildFieldClause(
        filters.jobFunctions,
        'department',
    )
    if (departmentClause) {
        mustClauses.push(departmentClause)
    }

    const positiveClause =
        mustClauses.length === 0
            ? '*:*'
            : mustClauses.length === 1
              ? mustClauses[0]
              : `(${mustClauses.join(' AND ')})`

    if (mustNotClauses.length === 0) {
        return positiveClause
    }

    const negativeClause = mustNotClauses
        .map((clause) => `NOT ${clause}`)
        .join(' AND ')

    return `${positiveClause} AND ${negativeClause}`
}
